// src/pages/UsersPage.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";

function normalizeUsersResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.rows)) return data.rows;
  return [];
}

function apiErrText(e) {
  const status = e?.response?.status;
  const body = e?.response?.data;
  const msg =
    body?.error ||
    body?.message ||
    (typeof body === "string" ? body : null) ||
    e?.message ||
    "Request failed";
  return status ? `${status}: ${msg}` : msg;
}

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
    display_name: "",
    is_active: true,
    is_superadmin: false,
  });

  const sorted = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
    return copy;
  }, [rows]);

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/admin/users");
      setRows(normalizeUsersResponse(data));
    } catch (e) {
      setMsg(apiErrText(e));
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    const payload = {
      email: String(form.email || "").trim(),
      password: String(form.password || ""),
      display_name: String(form.display_name || "").trim(),
      is_active: !!form.is_active,
      is_superadmin: !!form.is_superadmin, // ✅ send boolean; backend converts to "1"/"0"
    };

    if (!payload.email || !payload.password) {
      setMsg("Email + password required");
      return;
    }

    setBusy(true);
    setMsg("");
    try {
      await api.post("/admin/users", payload);
      setForm({ email: "", password: "", display_name: "", is_active: true, is_superadmin: false });
      await load();
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  // ✅ backend route is PATCH (not PUT)
  const patchUser = async (id, patch) => {
    setBusy(true);
    setMsg("");
    try {
      await api.patch(`/admin/users/${id}`, patch);
      await load();
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (id) => {
    const pw = window.prompt("New password (leave blank to cancel):");
    if (!pw) return;

    setBusy(true);
    setMsg("");
    try {
      await api.post(`/admin/users/${id}/reset-password`, { password: pw });
      setMsg("Password updated.");
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    setBusy(true);
    setMsg("");
    try {
      await api.delete(`/admin/users/${id}`);
      await load();
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Users</h2>
        <button className="btn-outline" onClick={load} disabled={busy}>
          Refresh
        </button>
      </div>

      {msg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {msg}
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Add user</div>

        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <input
            className="input"
            placeholder="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            className="input"
            placeholder="password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <input
            className="input"
            placeholder="display name"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </div>

        <div className="row" style={{ gap: 12, marginTop: 10, flexWrap: "wrap" }}>
          <label className="row" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>

          <label className="row" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={form.is_superadmin}
              onChange={(e) => setForm({ ...form, is_superadmin: e.target.checked })}
            />
            Superadmin
          </label>

          <button className="btn" onClick={create} disabled={busy || !form.email.trim() || !form.password}>
            Create
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Existing users</div>
        {busy ? <div className="muted">Loading…</div> : null}

        <div style={{ display: "grid", gap: 10 }}>
          {sorted.map((u) => {
            const active = String(u.is_active) === "1";
            const superadmin = String(u.is_superadmin) === "1";

            return (
              <div key={u.id} className="card" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>
                      {u.email} {superadmin ? <span className="muted">• superadmin</span> : null}
                    </div>
                    <div className="muted" style={{ marginTop: 4 }}>
                      id={u.id} • {u.display_name || "(no name)"} • active={String(u.is_active)} • superadmin={String(u.is_superadmin)}
                    </div>
                  </div>

                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <button
                      className="btn-outline"
                      disabled={busy}
                      onClick={() => patchUser(u.id, { is_active: !active })}
                    >
                      Toggle Active
                    </button>

                    <button
                      className="btn-outline"
                      disabled={busy}
                      onClick={() => patchUser(u.id, { is_superadmin: !superadmin })}
                    >
                      Toggle Superadmin
                    </button>

                    <button className="btn-outline" disabled={busy} onClick={() => resetPassword(u.id)}>
                      Reset Password
                    </button>

                    <button className="btn-outline" disabled={busy} onClick={() => del(u.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {!busy && sorted.length === 0 ? <div className="muted">No users found.</div> : null}
        </div>
      </div>
    </div>
  );
}
